<template>
  <header class="adm-page-header">
    <nav class="adm-page-header__crumbs" aria-label="Ruta de navegación">
      <template v-for="(crumb, i) in crumbs" :key="i">
        <router-link v-if="crumb.to" :to="crumb.to" class="adm-page-header__crumb">
          {{ crumb.label }}
        </router-link>
        <span v-else class="adm-page-header__crumb is-current">{{ crumb.label }}</span>
        <span v-if="i < crumbs.length - 1" class="adm-page-header__sep" aria-hidden="true">›</span>
      </template>
    </nav>

    <div class="adm-page-header__row">
      <div class="adm-page-header__titles">
        <h2 class="adm-title">{{ resolvedTitle }}</h2>
        <p v-if="description" class="adm-muted adm-page-header__desc">{{ description }}</p>
        <slot name="description" />
      </div>
      <div v-if="$slots.actions" class="adm-page-header__actions">
        <slot name="actions" />
      </div>
    </div>

    <slot name="tabs" />
  </header>
</template>

<script>
/**
 * Cabecera de vista administrativa — plan 27_09_2026 §4.2.
 *
 * Unifica el `<h1>` que cada vista estilaba por su cuenta y añade las migas,
 * que se derivan de `config/adminNav.js` (no se escriben a mano en la vista).
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { breadcrumbsFor, findNavItem } from '@/config/adminNav.js';

export default {
  name: 'AdminPageHeader',
  props: {
    /** Si se omite, se toma del árbol de navegación. */
    title: { type: String, default: '' },
    description: { type: String, default: '' },
  },
  setup(props) {
    const route = useRoute();
    const crumbs = computed(() => breadcrumbsFor(route));
    // Nombre distinto del prop `title`: el prop es la anulación manual y
    // esto es el valor efectivo (prop → árbol de navegación → meta de ruta).
    const resolvedTitle = computed(
      () => props.title || findNavItem(route)?.label || route.meta?.title || 'Administración',
    );
    return { crumbs, resolvedTitle };
  },
};
</script>

<style scoped>
.adm-page-header {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: var(--adm-gap-lg);
}

.adm-page-header__crumbs {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--color-text);
}

.adm-page-header__crumb {
  color: inherit;
  text-decoration: none;
}

.adm-page-header__crumb:not(.is-current):hover {
  color: var(--color-brand);
  text-decoration: underline;
}

.adm-page-header__crumb.is-current { color: var(--color-title); font-weight: 600; }

.adm-page-header__sep { opacity: 0.6; }

.adm-page-header__row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--adm-gap);
  flex-wrap: wrap;
}

.adm-page-header__titles { min-width: 0; }

.adm-page-header__desc { margin: 0.3rem 0 0 0; max-width: 70ch; }

.adm-page-header__actions {
  display: flex;
  align-items: center;
  gap: var(--adm-gap-sm);
  flex-wrap: wrap;
}
</style>
